/**
 * phraseMatch — where a phrase of the phrases course (ფრაზები, PHRASES.md)
 * sits in a sentence.
 *
 * ONE matcher for the app and the content pipeline: the phrase card marks
 * its phrase in the example sentence with it (PhraseIntro.tsx), and the
 * validator (scripts/phrases-rules.ts) asks it whether an item's sentence or
 * answer uses a phrase. Pure: no React, no I/O.
 *
 * A card writes its phrase in dictionary form; real sentences bend it.
 *
 *   slots        someone / somebody / something / sb / sth, and a "…" inside
 *                the phrase, stand for 1–4 words
 *                  pick someone up       ~ Can you pick me up?
 *                  Say hi to… for me.    ~ Say hi to Nino for me!
 *                  take sth for granted  ~ We take our parents for granted.
 *                A slot at the very end or start is not part of the phrase
 *                ("look after sb", "How about …?" mark "look after", "How about").
 *                A "…" written in the sentence fills a slot, so an option that
 *                is the citation form itself ("Say hi to… for me.") matches.
 *   possessives  my / your / one's / sb's … = any possessive (his, our, Nino's,
 *                the teacher's)
 *                  change your mind      ~ I changed my mind.
 *   objects      you / me after the phrase's first word = any object pronoun
 *                  Let me know.          ~ Let us know.
 *   reflexives   yourself = myself, themselves, …
 *   first verb   inflects (-s, -es, -ed, -ing, doubled consonant, irregular
 *                forms from irregularForms.ts) when the phrase starts with it,
 *                or when it follows a leading subject and words follow it
 *                  put on                ~ I'm putting on my glasses.
 *                  I think so.           ~ I thought so.  (but "I see." never ~ "I saw")
 *                The particle right after that verb may come after a short
 *                object: figure out ~ figure it out, turn on ~ turn the light on.
 *   I'm / It's … a leading subject + be takes any form of be, an optional
 *                "not" and question order (It's me. ~ Was it me?). With two
 *                or more words after it any subject pronoun fits (I'm running
 *                late. ~ We're running late.); with three or more it may be
 *                left out (I'm sorry to hear that. ~ Sorry to hear that.).
 *                A one-word formula keeps its subject: "You're welcome." is
 *                not "They're welcome", "I'm afraid…" is not "She was afraid".
 *
 * Case, curly apostrophes, contractions (I'm = I am, can't = cannot) and
 * punctuation do not matter — except that a match never runs across a
 * sentence break (. ! ? ; : …) the phrase itself does not have. Matches are
 * whole words. The phrase's own end punctuation and trailing "…" are not
 * part of it.
 */
import { IRREGULAR_VERB_TABLE } from './irregularForms.js';

/** `[start, end)` character offsets into the sentence as written. */
export type PhraseSpan = [start: number, end: number];

/* -------------------------------- word sets -------------------------------- */

const set = (s: string): ReadonlySet<string> => new Set(s.trim().split(/\s+/u));

/** Dictionary placeholders for "any words here". */
const SLOT_WORDS = set('someone somebody something sb sth smb smth');
/** Placeholders and determiners that stand for "anyone's". */
const POSS_WORDS = set("my your his her its our their one's someone's somebody's sb's sth's");
const REFLEXIVES = set(
  'myself yourself himself herself itself ourselves yourselves themselves oneself',
);
const OBJECTS = set('me you him her it us them');
/** "you" / "me" in a phrase stand for any object pronoun (not as its first word: "You too!", "Me neither."). */
const OBJECT_SLOTS = set('you me');
/** Subjects of a leading "I'm / It's …". */
const SUBJECTS = set('i you he she it we they that this there');
const BE = set('am is are was were');
/** Subject + 's that is "is" (it's = it is); any other X's is a possessive or ambiguous. */
const IS_HOSTS = set('it he she that this what where who how there here when why');
/** Particles of phrasal verbs that may follow a short object (figure it out). */
const PARTICLES = set(
  'up out off on in down away back over around round through about along apart aside together forward',
);
/** A short object between a verb and its particle: a pronoun, or a determiner + 1–2 words. */
const OBJECT_PRONOUNS = set('it them him her me us you this that');
const DETERMINERS = set('the a an my your his her its our their this that these those some');
/** Words that are never a phrase's "first verb" (pronouns, determiners,
 *  wh-words, adverbs, prepositions, conjunctions, modals). */
const NON_VERBS = set(`
  i you he she it we they me him her us them this that these those there here
  my your his its our their a an the some any no every each all both another other such
  what where when why who whom whose which how
  not never just so too very also always really well oh hey hi hello yes ok okay maybe now then
  first again still even only right
  in on at to for of with by about from up out off over into onto after before under as like than
  and but or if because
  can could will would shall should may might must
`);

/* ------------------------------- verb forms ------------------------------- */

const BASE_OF = new Map<string, string>();
const IRREGULAR_PAST = new Map<string, readonly string[]>();
for (const [base, past, part] of IRREGULAR_VERB_TABLE) {
  IRREGULAR_PAST.set(base, [past, part]);
  if (!BASE_OF.has(past)) BASE_OF.set(past, base);
  if (!BASE_OF.has(part)) BASE_OF.set(part, base);
}
/** Verbs whose present forms are irregular too. */
const SPECIAL_FORMS = new Map<string, readonly string[]>([
  ['be', ['am', 'is', 'are', 'was', 'were', 'been', 'being']],
  ['have', ['has', 'had', 'having']],
  ['do', ['does', 'did', 'done', 'doing']],
  ['go', ['goes', 'went', 'gone', 'going']],
]);
for (const [base, forms] of SPECIAL_FORMS) for (const f of forms) BASE_OF.set(f, base);

/** "makes" → "make", "watches" → "watch", "tries" → "try" (null: no -s). */
function withoutS(w: string): string | null {
  if (/[^aeiou]ies$/u.test(w)) return `${w.slice(0, -3)}y`;
  if (/(ss|x|z|ch|sh|o)es$/u.test(w)) return w.slice(0, -2);
  if (/[^s]s$/u.test(w) && w.length > 3) return w.slice(0, -1);
  return null;
}

/** The regular forms of `base`: -s / -es, -ed (unless irregular), -ing, with
 *  y → ie, a dropped e and a doubled final consonant (put → putting). */
function addRegularForms(base: string, out: Set<string>): void {
  const consonantY = /[^aeiou]y$/u.test(base);
  const doubled = /(^|[^aeiou])[aeiou][b-df-hj-np-tv-z]$/u.test(base)
    ? base + base.slice(-1)
    : null;
  out.add(
    consonantY
      ? `${base.slice(0, -1)}ies`
      : /(s|x|z|ch|sh|o)$/u.test(base)
        ? `${base}es`
        : `${base}s`,
  );
  if (!IRREGULAR_PAST.has(base)) {
    out.add(base.endsWith('e') ? `${base}d` : consonantY ? `${base.slice(0, -1)}ied` : `${base}ed`);
    if (doubled) out.add(`${doubled}ed`);
  }
  out.add(
    base.endsWith('ie')
      ? `${base.slice(0, -2)}ying`
      : /[^e]e$/u.test(base)
        ? `${base.slice(0, -1)}ing`
        : `${base}ing`,
  );
  if (doubled) out.add(`${doubled}ing`);
}

/** Every form the verb written `w` can take: base, -s, past, participle,
 *  -ing ("got" → get, gets, getting, got; "makes" → make, made, making …). */
export function verbForms(w: string): ReadonlySet<string> {
  const irregularBase = BASE_OF.get(w);
  const bases = irregularBase
    ? [irregularBase]
    : [w, withoutS(w)].filter((b): b is string => b !== null);
  const out = new Set([w]);
  for (const base of bases) {
    out.add(base);
    for (const f of IRREGULAR_PAST.get(base) ?? []) out.add(f);
    const special = SPECIAL_FORMS.get(base);
    if (special) for (const f of special) out.add(f);
    else addRegularForms(base, out);
  }
  return out;
}

/* -------------------------------- tokens -------------------------------- */

interface Tok {
  /** Lower case, straight apostrophe; a contraction is split (I'm → i + am). */
  w: string;
  /** Offsets of the WRITTEN word ("I'm" for both i and am). */
  start: number;
  end: number;
  /** A sentence break (. ! ? ; : …) comes right before this word. */
  brk: boolean;
}

const WORD = /[\p{L}\p{N}]+(?:['‘’-][\p{L}\p{N}]+)*/gu;
const BREAK = /[.!?;:…]/u;

/** A written word as the words it says: "can't" → can not, "it's" → it is,
 *  "let's" → let us, "we're" → we are. "Nino's" stays one (possessive). */
function expand(w: string): string[] {
  if (w === "can't" || w === 'cannot') return ['can', 'not'];
  if (w === "won't") return ['will', 'not'];
  if (w === "shan't") return ['shall', 'not'];
  if (w === "let's") return ['let', 'us'];
  const nt = /^(.+)n't$/u.exec(w);
  if (nt) return [nt[1], 'not'];
  const c = /^(.+)'(m|re|ve|ll|d|s)$/u.exec(w);
  if (!c) return [w];
  const [, host, tail] = c;
  switch (tail) {
    case 'm':
      return [host, 'am'];
    case 're':
      return [host, 'are'];
    case 've':
      return [host, 'have'];
    case 'll':
      return [host, 'will'];
    case 'd':
      return [host, 'would'];
    default:
      return IS_HOSTS.has(host) ? [host, 'is'] : [w];
  }
}

/** A written "…" between words: only a slot takes it, so an option that IS
 *  the citation form ("Say hi to… for me.") uses its own phrase. */
const ELLIPSIS_TOKEN = '…';
const ELLIPSIS = /…|\.\.\./u;

function tokenize(text: string): Tok[] {
  const out: Tok[] = [];
  let last = 0;
  for (const m of text.matchAll(WORD)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    let gap = text.slice(last, start);
    const dots = out.length > 0 ? ELLIPSIS.exec(gap) : null;
    if (dots) {
      const at = last + dots.index;
      out.push({
        w: ELLIPSIS_TOKEN,
        start: at,
        end: at + dots[0].length,
        brk: BREAK.test(gap.slice(0, dots.index)),
      });
      gap = gap.slice(dots.index + dots[0].length);
    }
    last = end;
    const brk = out.length > 0 && BREAK.test(gap);
    const parts = expand(m[0].toLowerCase().replace(/[‘’]/gu, "'"));
    parts.forEach((w, i) => out.push({ w, start, end, brk: brk && i === 0 }));
  }
  return out;
}

const isPossessiveWord = (w: string) => POSS_WORDS.has(w) || /[\p{L}]'s$/u.test(w);

/* ------------------------------- the pattern ------------------------------- */

/** One position of a compiled phrase. `brk`: the phrase itself has a
 *  sentence break before it, so the sentence may too. */
type El =
  | { k: 'word'; forms: ReadonlySet<string>; brk: boolean }
  | { k: 'slot'; brk: boolean }
  | { k: 'poss'; brk: boolean }
  | { k: 'obj'; brk: boolean }
  | { k: 'refl'; brk: boolean }
  | { k: 'subjBe'; subj: ReadonlySet<string>; brk: boolean }
  | { k: 'particle'; w: string; brk: boolean };

const SLOT = '\u0000slot';

/** The phrase's words with its "…" slots, end punctuation and outer slots dropped. */
function phraseWords(phrase: string): { w: string; brk: boolean }[] {
  const chunks = phrase.replace(/[‘’]/gu, "'").split(/\s*(?:…|\.\.\.)\s*/u);
  const raw: { w: string; brk: boolean }[] = [];
  chunks.forEach((chunk, i) => {
    if (i > 0) raw.push({ w: SLOT, brk: false });
    for (const t of tokenize(chunk)) raw.push({ w: SLOT_WORDS.has(t.w) ? SLOT : t.w, brk: t.brk });
  });
  while (raw.length && raw[0].w === SLOT) raw.shift();
  while (raw.length && raw[raw.length - 1].w === SLOT) raw.pop();
  return raw.filter((t, i) => !(t.w === SLOT && raw[i - 1]?.w === SLOT));
}

const compiled = new Map<string, El[][]>();

/** The phrase as patterns to try in order: as written, then the variants
 *  ("pick up Nino" for "pick someone up"; the phrase without its leading
 *  "I'm / It's" when three or more words follow it). */
function compile(phrase: string): El[][] {
  const hit = compiled.get(phrase);
  if (hit) return hit;
  const raw = phraseWords(phrase);
  const words = (from: number) => raw.slice(from).filter((t) => t.w !== SLOT).length;

  const subjBe = raw.length >= 3 && SUBJECTS.has(raw[0].w) && BE.has(raw[1].w);
  // The verb that may inflect: the phrase's first word, or the word after a
  // leading subject when more words follow it ("I think so", not "I see").
  let verbAt = -1;
  if (!subjBe && raw.length >= 2) {
    if (SUBJECTS.has(raw[0].w)) {
      if (raw.length >= 3 && raw[1].w !== SLOT && !NON_VERBS.has(raw[1].w)) verbAt = 1;
    } else if (raw[0].w !== SLOT && !NON_VERBS.has(raw[0].w) && raw[0].w !== 'be') {
      verbAt = 0; // imperative "Be quiet." never becomes "is quiet"
    }
  }

  const els: El[] = [];
  const restFrom = subjBe ? 2 : 0;
  if (subjBe) {
    // A one-word formula keeps its subject ("You're welcome", "I'm full").
    els.push({
      k: 'subjBe',
      subj: words(2) >= 2 ? SUBJECTS : new Set([raw[0].w]),
      brk: raw[0].brk,
    });
  }
  for (let i = restFrom; i < raw.length; i++) {
    const { w, brk } = raw[i];
    if (w === SLOT) els.push({ k: 'slot', brk });
    else if (POSS_WORDS.has(w)) els.push({ k: 'poss', brk });
    else if (REFLEXIVES.has(w)) els.push({ k: 'refl', brk });
    else if (i > 0 && OBJECT_SLOTS.has(w)) els.push({ k: 'obj', brk });
    else if (i === verbAt) els.push({ k: 'word', forms: verbForms(w), brk });
    else if (verbAt >= 0 && i === verbAt + 1 && PARTICLES.has(w))
      els.push({ k: 'particle', w, brk });
    else els.push({ k: 'word', forms: new Set([w]), brk });
  }

  const out: El[][] = els.length ? [els] : [];
  // pick someone up → pick up someone (the slot then trails, so it drops).
  const v = verbAt - restFrom;
  if (verbAt >= 0 && els[v + 1]?.k === 'slot') {
    const after = els[v + 2];
    if (after?.k === 'word' && after.forms.size === 1) {
      const particle = [...after.forms][0];
      if (PARTICLES.has(particle)) {
        const alt: El[] = [
          ...els.slice(0, v + 1),
          { k: 'particle', w: particle, brk: after.brk },
          ...els.slice(v + 2 + 1),
        ];
        if (els.length > v + 3) alt.splice(v + 2, 0, els[v + 1]);
        out.push(alt);
      }
    }
  }
  // "I'm sorry to hear that." → "Sorry to hear that."
  if (subjBe && words(2) >= 3) out.push(els.slice(1));
  compiled.set(phrase, out);
  return out;
}

/* -------------------------------- matching -------------------------------- */

/** Token index after a match of `p` from `pi` at token `ti`, or -1. Only the
 *  match's first token may follow a sentence break the phrase does not have. */
function matchFrom(p: El[], pi: number, t: Tok[], ti: number, first: number): number {
  if (pi === p.length) return ti;
  const el = p[pi];
  /** Token j may be taken as the element's own first word (k = 0) or a later one. */
  const free = (j: number, k = 0) =>
    j < t.length && (j === first || !t[j].brk || (k === 0 && el.brk));
  const next = (j: number) => matchFrom(p, pi + 1, t, j, first);
  const w = (j: number) => t[j]?.w ?? '';

  switch (el.k) {
    case 'word':
      return free(ti) && el.forms.has(w(ti)) ? next(ti + 1) : -1;
    case 'obj':
      return free(ti) && OBJECTS.has(w(ti)) ? next(ti + 1) : -1;
    case 'refl':
      return free(ti) && REFLEXIVES.has(w(ti)) ? next(ti + 1) : -1;
    case 'slot': {
      for (let n = 1; n <= 4; n++) {
        if (!free(ti + n - 1, n - 1)) return -1;
        const r = next(ti + n);
        if (r >= 0) return r;
      }
      return -1;
    }
    case 'poss': {
      if (!free(ti)) return -1;
      if (isPossessiveWord(w(ti))) {
        const r = next(ti + 1);
        if (r >= 0) return r;
      }
      // "the teacher's", "my mum's"
      return DETERMINERS.has(w(ti)) && free(ti + 1, 1) && /[\p{L}]'s$/u.test(w(ti + 1))
        ? next(ti + 2)
        : -1;
    }
    case 'particle': {
      if (!free(ti)) return -1;
      if (w(ti) === el.w) {
        const r = next(ti + 1);
        if (r >= 0) return r;
      }
      // figure it out · turn the light on · put your new jacket on
      const gap = OBJECT_PRONOUNS.has(w(ti)) ? [1] : DETERMINERS.has(w(ti)) ? [2, 3] : [];
      for (const n of gap) {
        let ok = true;
        for (let k = 1; k <= n; k++) ok = ok && free(ti + k, k);
        if (ok && w(ti + n) === el.w) {
          const r = next(ti + n + 1);
          if (r >= 0) return r;
        }
      }
      return -1;
    }
    case 'subjBe': {
      if (!free(ti)) return -1;
      const notThen = (j: number, then: (j: number) => number) => {
        if (w(j) === 'not' && free(j, 1)) {
          const r = then(j + 1);
          if (r >= 0) return r;
        }
        return then(j);
      };
      // It's / It isn't / It was …
      if (el.subj.has(w(ti)) && free(ti + 1, 1) && BE.has(w(ti + 1))) {
        const r = notThen(ti + 2, next);
        if (r >= 0) return r;
      }
      // Is it … / Isn't it … / Are you …
      if (BE.has(w(ti))) {
        return notThen(ti + 1, (j) => (free(j, 1) && el.subj.has(w(j)) ? next(j + 1) : -1));
      }
      return -1;
    }
  }
}

const tokenCache = new Map<string, Tok[]>();
function tokensOf(sentence: string): Tok[] {
  let t = tokenCache.get(sentence);
  if (!t) {
    if (tokenCache.size > 5000) tokenCache.clear();
    t = tokenize(sentence);
    tokenCache.set(sentence, t);
  }
  return t;
}

function scan(sentence: string, phrase: string, all: boolean): PhraseSpan[] {
  const patterns = compile(phrase);
  const out: PhraseSpan[] = [];
  if (patterns.length === 0) return out;
  const t = tokensOf(sentence);
  let ti = 0;
  while (ti < t.length) {
    let end = -1;
    for (const p of patterns) {
      end = matchFrom(p, 0, t, ti, ti);
      if (end >= 0) break;
    }
    if (end > ti) {
      out.push([t[ti].start, t[end - 1].end]);
      if (!all) return out;
      ti = end;
    } else {
      ti++;
    }
  }
  return out;
}

/**
 * Where `phrase` sits in `sentence` — `[start, end)` of the first use, from
 * its first word to its last (slot words inside included) — or null.
 */
export function findPhrase(sentence: string, phrase: string): PhraseSpan | null {
  return scan(sentence, phrase, false)[0] ?? null;
}

/** Every use of `phrase` in `sentence`, left to right, not overlapping. */
export function findPhraseAll(sentence: string, phrase: string): PhraseSpan[] {
  return scan(sentence, phrase, true);
}

/** Does `sentence` use `phrase`? */
export function containsPhrase(sentence: string, phrase: string): boolean {
  return scan(sentence, phrase, false).length > 0;
}
